import React from 'react';
import Header from 'components/ui/Header';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import Button from 'components/ui/Button';

const BusinessTherapyProcessPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-purple-50 to-indigo-50 pt-20 pb-24 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-32 h-32 bg-blue-600 rounded-full"></div>
          <div className="absolute top-40 right-20 w-24 h-24 bg-purple-600 rounded-full"></div>
          <div className="absolute bottom-20 left-1/4 w-16 h-16 bg-indigo-600 rounded-full"></div>
          <div className="absolute bottom-40 right-1/3 w-20 h-20 bg-violet-600 rounded-full"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-800 px-6 py-3 rounded-full text-sm font-semibold mb-6">
              <Icon name="Heart" size={20} />
              <span>Processus de Business Thérapie</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 leading-tight mb-8">
              Méthodologie Thérapeutique{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                Structurée
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
              Un processus systématique en 5 phases pour guider votre organisation vers la guérison complète et la transformation durable
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Process Phases */}
        <section className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Les 5 Phases Thérapeutiques
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Chaque phase est conçue pour adresser des aspects spécifiques de la guérison organisationnelle
            </p>
          </div>

          {/* Phase Cards */}
          <div className="space-y-12">
            {/* Phase 1 */}
            <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold">
                      1
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">Diagnostic Initial</h3>
                      <p className="text-blue-600 font-semibold">Évaluation Complète</p>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed">
                    Analyse approfondie des symptômes organisationnels, identification des blocages systémiques et cartographie des zones de dysfonctionnement. Cette phase établit le diagnostic complet de l'état de santé de votre organisation.
                  </p>
                  
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900">Livrables de cette phase :</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center space-x-2">
                        <Icon name="CheckCircle" size={16} className="text-green-500" />
                        <span>Rapport de diagnostic organisationnel complet</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Icon name="CheckCircle" size={16} className="text-green-500" />
                        <span>Cartographie des dysfonctionnements identifiés</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Icon name="CheckCircle" size={16} className="text-green-500" />
                        <span>Priorisation des interventions thérapeutiques</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl p-8 h-80 flex items-center justify-center">
                    <Icon name="Search" size={120} className="text-blue-600" />
                  </div>
                  <div className="absolute -bottom-4 -right-4 bg-white rounded-xl p-3 shadow-lg">
                    <div className="text-sm font-semibold text-gray-900">Durée: 2-3 semaines</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Phase 2 */}
            <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="lg:order-2 space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center text-xl font-bold">
                      2
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">Intervention de Crise</h3>
                      <p className="text-purple-600 font-semibold">Stabilisation Immédiate</p>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed">
                    Intervention d'urgence pour stabiliser les situations critiques, arrêter la dégradation et mettre en place des mesures conservatoires. Cette phase vise à stopper l'hémorragie organisationnelle.
                  </p>
                  
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900">Actions thérapeutiques :</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center space-x-2">
                        <Icon name="Zap" size={16} className="text-yellow-500" />
                        <span>Interventions d'urgence ciblées</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Icon name="Zap" size={16} className="text-yellow-500" />
                        <span>Mise en place de protocoles de stabilisation</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Icon name="Zap" size={16} className="text-yellow-500" />
                        <span>Communication de crise et gestion des parties prenantes</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="lg:order-1 relative">
                  <div className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl p-8 h-80 flex items-center justify-center">
                    <Icon name="Activity" size={120} className="text-purple-600" />
                  </div>
                  <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-3 shadow-lg">
                    <div className="text-sm font-semibold text-gray-900">Durée: 1-2 semaines</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Phase 3 */}
            <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xl font-bold">
                      3
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">Réalignement Stratégique</h3>
                      <p className="text-indigo-600 font-semibold">Restructuration Thérapeutique</p>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed">
                    Redéfinition des structures, processus et systèmes organisationnels pour aligner l'ensemble sur la nouvelle vision de santé. Phase de reconstruction thérapeutique profonde.
                  </p>
                  
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900">Transformations réalisées :</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center space-x-2">
                        <Icon name="Settings" size={16} className="text-indigo-500" />
                        <span>Restructuration des processus critiques</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Icon name="Settings" size={16} className="text-indigo-500" />
                        <span>Réalignement des équipes et responsabilités</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Icon name="Settings" size={16} className="text-indigo-500" />
                        <span>Mise en place de nouveaux indicateurs de santé</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-xl p-8 h-80 flex items-center justify-center">
                    <Icon name="Target" size={120} className="text-indigo-600" />
                  </div>
                  <div className="absolute -bottom-4 -right-4 bg-white rounded-xl p-3 shadow-lg">
                    <div className="text-sm font-semibold text-gray-900">Durée: 4-6 semaines</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Phase 4 */}
            <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="lg:order-2 space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-violet-600 text-white rounded-full flex items-center justify-center text-xl font-bold">
                      4
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">Support d'Implémentation</h3>
                      <p className="text-violet-600 font-semibold">Accompagnement Continu</p>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed">
                    Accompagnement intensif durant la mise en œuvre des changements, monitoring continu des progrès et ajustements thérapeutiques selon l'évolution de l'organisation.
                  </p>
                  
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900">Support fourni :</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center space-x-2">
                        <Icon name="Users" size={16} className="text-violet-500" />
                        <span>Coaching des équipes de direction</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Icon name="Users" size={16} className="text-violet-500" />
                        <span>Formation des managers aux nouvelles pratiques</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Icon name="Users" size={16} className="text-violet-500" />
                        <span>Suivi hebdomadaire des indicateurs de santé</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="lg:order-1 relative">
                  <div className="bg-gradient-to-br from-violet-100 to-violet-200 rounded-xl p-8 h-80 flex items-center justify-center">
                    <Icon name="Users" size={120} className="text-violet-600" />
                  </div>
                  <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-3 shadow-lg">
                    <div className="text-sm font-semibold text-gray-900">Durée: 8-12 semaines</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Phase 5 */}
            <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center text-xl font-bold">
                      5
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">Récupération Durable</h3>
                      <p className="text-green-600 font-semibold">Immunité Organisationnelle</p>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed">
                    Consolidation des acquis thérapeutiques, développement de l'immunité organisationnelle et mise en place de mécanismes de prévention pour éviter les rechutes futures.
                  </p>
                  
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900">Résultats durables :</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center space-x-2">
                        <Icon name="Shield" size={16} className="text-green-500" />
                        <span>Système d'immunité organisationnelle renforcé</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Icon name="Shield" size={16} className="text-green-500" />
                        <span>Protocoles de prévention automatisés</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Icon name="Shield" size={16} className="text-green-500" />
                        <span>Capacité d'auto-régulation développée</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="bg-gradient-to-br from-green-100 to-green-200 rounded-xl p-8 h-80 flex items-center justify-center">
                    <Icon name="Shield" size={120} className="text-green-600" />
                  </div>
                  <div className="absolute -bottom-4 -right-4 bg-white rounded-xl p-3 shadow-lg">
                    <div className="text-sm font-semibold text-gray-900">Durée: 4-6 semaines</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Success Metrics */}
        <section className="mb-20">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 lg:p-12 text-white">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Résultats Thérapeutiques Mesurés
              </h2>
              <p className="text-xl text-blue-100">
                L'efficacité de notre processus démontrée par les chiffres
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">95%</div>
                <div className="text-blue-100">Taux de guérison complète</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">3-6</div>
                <div className="text-blue-100">Mois de traitement moyen</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">80%</div>
                <div className="text-blue-100">Amélioration performance</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">90%</div>
                <div className="text-blue-100">Prévention des rechutes</div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Prêt à Démarrer Votre Thérapie Organisationnelle ?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Contactez nos thérapeutes spécialisés pour un diagnostic gratuit et découvrez comment notre processus peut transformer votre organisation
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-semibold shadow-lg transition-all duration-200 transform hover:scale-105"
              >
                <div className="flex items-center space-x-2">
                  <Icon name="Calendar" size={20} />
                  <span>Consultation Gratuite</span>
                </div>
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-4 rounded-xl font-semibold transition-all duration-200"
              >
                <div className="flex items-center space-x-2">
                  <Icon name="Download" size={20} />
                  <span>Guide Méthodologique</span>
                </div>
              </Button>
            </div>
          </div>
        </section>
      </div>
      
      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <Image
                  src="/assets/images/logo_CRF_PERFECTION_4x-1761998753526.png"
                  alt="CRF PERFECTION logo featuring interlocking heart-shaped design in red and gold colors representing Centre de Recherche et de Formation business therapy excellence"
                  className="w-12 h-12 object-contain"
                />
                <div>
                  <h3 className="text-xl font-bold">CRF PERFECTION</h3>
                  <p className="text-gray-400 text-sm">Processus de Business Thérapie</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                Méthodologie structurée pour transformer les crises organisationnelles en opportunités de croissance durable.
              </p>
            </div>

            {/* Process Phases */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white">Phases Thérapeutiques</h4>
              <ul className="space-y-2">
                <li>
                  <a href="#diagnosis" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                    Diagnostic Initial
                  </a>
                </li>
                <li>
                  <a href="#crisis-intervention" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                    Intervention de Crise
                  </a>
                </li>
                <li>
                  <a href="#strategic-realignment" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                    Réalignement Stratégique
                  </a>
                </li>
                <li>
                  <a href="#implementation-support" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                    Support d'Implémentation
                  </a>
                </li>
                <li>
                  <a href="#sustainable-recovery" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                    Récupération Durable
                  </a>
                </li>
              </ul>
            </div>

            {/* Methodology Resources */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white">Ressources Méthodologiques</h4>
              <ul className="space-y-2">
                <li>
                  <a href="#assessment-tools" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                    Outils d'Évaluation
                  </a>
                </li>
                <li>
                  <a href="#intervention-protocols" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                    Protocoles d'Intervention
                  </a>
                </li>
                <li>
                  <a href="#case-studies" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                    Études de Cas
                  </a>
                </li>
                <li>
                  <a href="#success-metrics" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                    Métriques de Succès
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact Process */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white">Démarrer le Processus</h4>
              <div className="space-y-3">
                <a href="/contact" className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                  <Icon name="MessageSquare" size={16} />
                  <span>Consultation Initiale</span>
                </a>
                <a href="#diagnostic-gratuit" className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                  <Icon name="Search" size={16} />
                  <span>Diagnostic Gratuit</span>
                </a>
                <a href="#methodology-guide" className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                  <Icon name="BookOpen" size={16} />
                  <span>Guide Méthodologique</span>
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="border-t border-gray-800 mt-12 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <p className="text-sm text-gray-500 mb-4 md:mb-0">
                © 2024 CRF Perfection - Processus de Business Thérapie. Tous droits réservés.
              </p>
              <div className="flex space-x-6">
                <a href="/privacy" className="text-sm text-gray-500 hover:text-white transition-colors duration-200">
                  Politique de Confidentialité
                </a>
                <a href="/methodology-ethics" className="text-sm text-gray-500 hover:text-white transition-colors duration-200">
                  Éthique Méthodologique
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BusinessTherapyProcessPage;